import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin } from 'rxjs';
import { WnPerms } from '../../../../core/auth/worknest-admin-permissions';
import { apiErrorFallback, extractApiErrorMessage } from '../../../../core/http/extract-api-error-message';
import { Translation } from '../../../../core/i18n/translation.service';
import type {
  AbsenceImpactAction,
  AttendancePolicyOptions,
  HourlyLeavePermissionOptions,
  PayrollPeriodOptions,
  TenantOperationalConfigurationRoot,
} from '../../../../core/models/tenant-organization/tenant-operational-configuration.model';
import type { TenantSubscriptionSummaryDto } from '../../../../core/models/tenant-organization/tenant-subscription.models';
import { PermissionService } from '../../../../core/services/permission.service';
import { TenantOrganizationService } from '../../../../core/services/tenant-organization.service';
import { ToastFeedbackService } from '../../../../core/services/toast-feedback.service';
import { StatusChip } from '../../../../shared/components/status-chip/status-chip';
import { WnIfPermissionDirective } from '../../../../shared/directives/wn-if-permission.directive';
import { markPendingChangesSaved } from '../../../../shared/guards/pending-changes.guard';
import { localizedCurrencyLabel } from '../../../../shared/utils/currency-label.util';

import { WnFocusInvalidOnSubmitDirective } from '../../../../shared/directives/wn-focus-invalid-on-submit.directive';
import { AiAgentsSettingsCardComponent } from './ai-agents-settings-card/ai-agents-settings-card.component';
type AttendanceRatioControlName = 'halfDayMinWorkedRatio' | 'absentCheckInAfterRatio';

type NumericOption = {
  readonly value: number;
  readonly labelKey: string;
  readonly params?: Record<string, number | string>;
};

type PayrollTimeZoneOption = {
  readonly value: string;
  readonly labelKey: string;
  readonly fallbackLabel?: string;
};

@Component({
  selector: 'app-organization-settings-page',
  standalone: true,
  imports: [WnFocusInvalidOnSubmitDirective, ReactiveFormsModule, TranslatePipe, ButtonModule, TooltipModule, WnIfPermissionDirective, StatusChip, AiAgentsSettingsCardComponent],
  templateUrl: './organization-settings-page.component.html',
  styleUrl: './organization-settings-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationSettingsPageComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly api = inject(TenantOrganizationService);
  private readonly translation = inject(Translation);
  private readonly permissions = inject(PermissionService);
  private readonly toast = inject(ToastFeedbackService);

  /** For `*wnIfPermission` (save operational JSON). */
  protected readonly orgSettingsManageKeys = WnPerms.organization.manage;

  protected readonly canManage = computed(() => this.permissions.hasAny(WnPerms.organization.manage));

  protected readonly pageLoading = signal(true);
  protected readonly pageError = signal<string | null>(null);
  protected readonly saveError = signal<string | null>(null);
  protected readonly saving = signal(false);
  protected readonly subscription = signal<TenantSubscriptionSummaryDto | null>(null);
  protected readonly billingPeriods = [1, 12];
  protected readonly attendanceRatioBaseOptions: readonly NumericOption[] = [
    { value: 0.25, labelKey: 'ORG_SETTINGS.RATIO_QUARTER_SHIFT' },
    { value: 0.5, labelKey: 'ORG_SETTINGS.RATIO_HALF_SHIFT' },
    { value: 0.75, labelKey: 'ORG_SETTINGS.RATIO_THREE_QUARTER_SHIFT' },
    { value: 1, labelKey: 'ORG_SETTINGS.RATIO_FULL_SHIFT' },
  ];
  protected readonly faceAccuracyBaseOptions: readonly NumericOption[] = [
    { value: 0.56, labelKey: 'ORG_SETTINGS.FACE_ACCURACY_LOW' },
    { value: 0.62, labelKey: 'ORG_SETTINGS.FACE_ACCURACY_BALANCED' },
    { value: 0.72, labelKey: 'ORG_SETTINGS.FACE_ACCURACY_STRICT' },
  ];
  protected readonly absenceImpactActionOptions: readonly NumericOption[] = [
    { value: 1, labelKey: 'ORG_SETTINGS.ABSENCE_IMPACT_DEDUCT_ANNUAL_LEAVE' },
    { value: 2, labelKey: 'ORG_SETTINGS.ABSENCE_IMPACT_DEDUCT_SALARY_DAY' },
    { value: 3, labelKey: 'ORG_SETTINGS.ABSENCE_IMPACT_DO_NOTHING' },
  ];
  protected readonly absenceFallbackActionOptions: readonly NumericOption[] = [
    { value: 2, labelKey: 'ORG_SETTINGS.ABSENCE_IMPACT_DEDUCT_SALARY_DAY' },
    { value: 3, labelKey: 'ORG_SETTINGS.ABSENCE_IMPACT_DO_NOTHING' },
  ];
  private readonly payrollTimeZoneBaseOptions: readonly PayrollTimeZoneOption[] = [
    { value: 'Africa/Cairo', labelKey: 'ORG_SETTINGS.TIME_ZONE_AFRICA_CAIRO' },
    { value: 'Asia/Riyadh', labelKey: 'ORG_SETTINGS.TIME_ZONE_ASIA_RIYADH' },
    { value: 'UTC', labelKey: 'ORG_SETTINGS.TIME_ZONE_UTC' },
  ];
  private readonly defaultOutsideGraceMinutes = 15;
  /** Share of the scheduled time each threshold falls back to when no hours are set (shown in the hints). */
  protected readonly halfDayFallbackPercent = signal(50);
  protected readonly workdayFallbackPercent = signal(75);
  private operationalBaseline = '';

  protected readonly form = this.fb.group({
    attendance: this.fb.group({
      policy: this.fb.group({
        lateGraceMinutes: this.fb.control(0),
        earlyLeaveGraceMinutes: this.fb.control(0),
        earliestCheckInMinutesBeforeShift: this.fb.control<number | null>(30, [Validators.min(0), Validators.max(1440)]),
        halfDayMinWorkedRatio: this.fb.control(0.5),
        absentCheckInAfterRatio: this.fb.control(0.75),
        halfDayMinWorkedHours: this.fb.control<number | null>(null, [Validators.min(0.25), Validators.max(24)]),
        workdayMinWorkedHours: this.fb.control<number | null>(null, [Validators.min(0.25), Validators.max(24)]),
      }, {
        validators: [
          OrganizationSettingsPageComponent.attendancePolicyRatioValidator,
          OrganizationSettingsPageComponent.attendanceHoursOrderValidator,
        ],
      }),
      geofence: this.fb.group({
        outsideGraceMinutes: this.fb.control(0),
      }),
      faceRecognition: this.fb.group({
        minCosineSimilarity: this.fb.control(0.62),
        requireLiveness: this.fb.control(true),
      }),
      absenceJob: this.fb.group({
        enabled: this.fb.control(true),
      }),
      absenceImpact: this.fb.group({
        action: this.fb.control<AbsenceImpactAction>(2),
        insufficientLeaveFallbackAction: this.fb.control<AbsenceImpactAction | null>(2),
      }, { validators: [OrganizationSettingsPageComponent.absenceImpactFallbackValidator] }),
      travelMission: this.fb.group({
        enabled: this.fb.control(true),
        createOnOffDays: this.fb.control(false),
        skipIfAttendanceExists: this.fb.control(true),
        useShiftWindow: this.fb.control(true),
      }),
      hourlyLeavePermission: this.fb.group({
        enabled: this.fb.control(false),
        monthlyQuotaMinutes: this.fb.control(240),
        minRequestMinutes: this.fb.control(60),
        maxRequestMinutes: this.fb.control(120),
        requireReason: this.fb.control(false),
        allowPastDates: this.fb.control(false),
        maxPastDays: this.fb.control<number | null>(null, [Validators.min(1)]),
        allowFutureDates: this.fb.control(true),
        maxFutureDays: this.fb.control<number | null>(null, [Validators.min(1)]),
      }),
    }),
    requests: this.fb.group({
      leaveSubmission: this.fb.group({
        allowPastDates: this.fb.control(true),
        maxPastDays: this.fb.control<number | null>(null),
      }),
    }),
    payroll: this.fb.group({
      cycleStartDay: this.fb.control(1),
      useLastDayFallback: this.fb.control(true),
      generationLocalTime: this.fb.control('03:00'),
      timeZoneId: this.fb.control('Africa/Cairo'),
      labelMode: this.fb.control<1>(1),
      autoGenerateAtPeriodStart: this.fb.control(true),
    }),
  });

  constructor() {
    this.form.controls.attendance.controls.travelMission.controls.enabled.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((enabled) => this.syncTravelMissionDependentControls(enabled));

    // A day limit only applies to an allowed direction; clear it when the direction is switched off so a hidden,
    // stale value can neither block saving nor come back later.
    const hlp = this.form.controls.attendance.controls.hourlyLeavePermission.controls;
    hlp.allowPastDates.valueChanges.pipe(takeUntilDestroyed()).subscribe((allowed) => {
      if (!allowed) hlp.maxPastDays.setValue(null);
    });
    hlp.allowFutureDates.valueChanges.pipe(takeUntilDestroyed()).subscribe((allowed) => {
      if (!allowed) hlp.maxFutureDays.setValue(null);
    });

    this.pageLoading.set(true);
    forkJoin({
      op: this.api.getOperationalSettings(),
      sub: this.api.getSubscription(),
    }).subscribe({
      next: ({ op, sub }) => {
        this.halfDayFallbackPercent.set(this.ratioPercent(op.attendance.policy.halfDayMinWorkedRatio, 50));
        this.workdayFallbackPercent.set(this.ratioPercent(op.attendance.policy.workdayMinWorkedRatio, 75));
        this.form.patchValue({
          attendance: {
            policy: this.editablePolicy(this.normalizeAttendancePolicy(op.attendance.policy)),
            geofence: { ...op.attendance.geofence },
            faceRecognition: { ...op.attendance.faceRecognition },
            absenceJob: {
              enabled: op.attendance.absenceJob.enabled,
            },
            absenceImpact: {
              action: op.attendance.absenceImpact?.action ?? 2,
              insufficientLeaveFallbackAction: op.attendance.absenceImpact?.insufficientLeaveFallbackAction ?? 2,
            },
            travelMission: { ...op.attendance.travelMission },
            hourlyLeavePermission: {
              ...this.defaultHourlyLeavePermission(),
              ...(op.attendance.hourlyLeavePermission ?? {}),
            },
          },
          requests: {
            leaveSubmission: {
              allowPastDates: op.requests.leaveSubmission.allowPastDates,
              maxPastDays: op.requests.leaveSubmission.maxPastDays,
            },
          },
          payroll: {
            ...this.defaultPayrollPeriodOptions(),
            ...(op.payroll ?? {}),
          },
        });
        this.syncTravelMissionDependentControls(op.attendance.travelMission.enabled, false);

        this.subscription.set(sub);
        this.capturePendingChangesBaseline();
        this.pageLoading.set(false);
      },
      error: (err) => {
        this.pageError.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
        this.pageLoading.set(false);
      },
    });
  }

  protected formatMoney(value: number | null | undefined, currency?: string | null): string {
    const amount = new Intl.NumberFormat(this.numberDisplayLocale(), {
      maximumFractionDigits: 2,
    }).format(Number(value ?? 0));
    return `${amount} ${this.currencyLabel(currency)}`;
  }

  protected formatSubscriptionDateTime(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(this.gregorianDisplayLocale(), {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(parsed);
  }

  private currencyLabel(currency?: string | null, market?: number | null): string {
    return localizedCurrencyLabel(this.translation, currency, market);
  }

  private numberDisplayLocale(): string {
    return this.translation.getCurrentLang() === 'ar-SA'
      ? 'ar-SA-u-nu-latn'
      : this.translation.getCurrentLang();
  }

  private gregorianDisplayLocale(): string {
    return this.translation.getCurrentLang() === 'ar-SA'
      ? 'ar-SA-u-ca-gregory-nu-latn'
      : this.translation.getCurrentLang();
  }

  protected currentPackageTitle(): string {
    const s = this.subscription();
    if (!s) {
      return '';
    }
    return this.translation.getCurrentLang() === 'ar-SA' ? (s.packageTitleAr ?? '') : (s.packageTitleEn ?? '');
  }

  protected billingPeriodLabel(months: number): string {
    const safeMonths = this.billingPeriods.includes(months) ? months : 1;
    return this.translation.instant(`ORG_SETTINGS.BILLING_PERIOD_${safeMonths}`);
  }

  protected ratioOptions(controlName: AttendanceRatioControlName): readonly NumericOption[] {
    const policy = this.form.controls.attendance.controls.policy.controls;
    const current = Number(policy[controlName].value);
    const options = this.withCurrentNumericOption(current, this.attendanceRatioBaseOptions, 'ORG_SETTINGS.RATIO_CURRENT');
    if (controlName !== 'absentCheckInAfterRatio') {
      return options;
    }
    // The half-day share is no longer edited here (WN-916 moved it to hours), so only offer absence points the saved
    // half-day share allows; the current value always stays selectable.
    const halfDay = Number(policy.halfDayMinWorkedRatio.value);
    return Number.isFinite(halfDay)
      ? options.filter((option) => option.value > halfDay || Math.abs(option.value - current) < 0.0001)
      : options;
  }

  protected faceAccuracyOptions(): readonly NumericOption[] {
    const current = Number(this.form.controls.attendance.controls.faceRecognition.controls.minCosineSimilarity.value);
    return this.withCurrentNumericOption(current, this.faceAccuracyBaseOptions, 'ORG_SETTINGS.FACE_ACCURACY_CURRENT');
  }

  protected numericOptionLabel(option: NumericOption): string {
    return this.translation.instant(option.labelKey, option.params);
  }

  protected payrollTimeZoneOptions(): readonly PayrollTimeZoneOption[] {
    const current = String(this.form.controls.payroll.controls.timeZoneId.value || '').trim();
    if (!current || this.payrollTimeZoneBaseOptions.some((option) => option.value === current)) {
      return this.payrollTimeZoneBaseOptions;
    }

    return [
      { value: current, labelKey: '', fallbackLabel: current },
      ...this.payrollTimeZoneBaseOptions,
    ];
  }

  protected payrollTimeZoneOptionLabel(option: PayrollTimeZoneOption): string {
    return option.labelKey ? this.translation.instant(option.labelKey) : (option.fallbackLabel ?? option.value);
  }

  protected attendancePolicyRatioInvalid(): boolean {
    const policy = this.form.controls.attendance.controls.policy;
    return policy.hasError('workdayRatioNotGreaterThanHalfDay') && (policy.dirty || policy.touched);
  }

  protected absenceImpactAnnualLeaveSelected(): boolean {
    return Number(this.form.controls.attendance.controls.absenceImpact.controls.action.value) === 1;
  }

  protected absenceImpactFallbackInvalid(): boolean {
    const group = this.form.controls.attendance.controls.absenceImpact;
    return group.hasError('absenceFallbackRequired') && (group.dirty || group.touched);
  }

  protected outsideGeofenceAllowed(): boolean {
    const value = Number(this.form.controls.attendance.controls.geofence.controls.outsideGraceMinutes.value);
    return Number.isFinite(value) && value > 0;
  }

  protected travelMissionEnabled(): boolean {
    return Boolean(this.form.controls.attendance.controls.travelMission.controls.enabled.value);
  }

  protected onOutsideGeofenceToggle(event: Event): void {
    const checked = Boolean((event.target as HTMLInputElement | null)?.checked);
    const control = this.form.controls.attendance.controls.geofence.controls.outsideGraceMinutes;
    const current = Number(control.value);
    const nextValue = checked
      ? (Number.isFinite(current) && current > 0 ? current : this.defaultOutsideGraceMinutes)
      : 0;
    control.setValue(nextValue);
    control.markAsDirty();
    control.markAsTouched();
  }

  protected summaryBillingPeriodMonths(sub: TenantSubscriptionSummaryDto): number {
    return this.billingPeriods.includes(sub.billingPeriodMonths) ? sub.billingPeriodMonths : 1;
  }

  protected summaryLicensedUsers(sub: TenantSubscriptionSummaryDto): number {
    return sub.licensedUsers || sub.maxUsers || sub.currentUsers || 0;
  }

  protected summaryMonthlyTotal(sub: TenantSubscriptionSummaryDto): number {
    return sub.monthlyTotalPrice || sub.totalPrice || sub.price || 0;
  }

  protected formatNumber(value: number): string {
    return new Intl.NumberFormat(this.numberDisplayLocale(), {
      maximumFractionDigits: 2,
    }).format(value);
  }

  protected summaryPricingBandLabel(sub: TenantSubscriptionSummaryDto): string {
    if (sub.pricingBandMinimumUsers <= 0 && sub.pricingBandMaximumUsers == null) {
      return this.formatNumber(this.summaryBillableUsers(sub));
    }

    return this.pricingBandRangeLabel(sub.pricingBandMinimumUsers, sub.pricingBandMaximumUsers);
  }

  protected summaryBillableUsers(sub: TenantSubscriptionSummaryDto): number {
    return sub.billableUsers || sub.pricingBandMinimumUsers || this.summaryLicensedUsers(sub);
  }

  private pricingBandRangeLabel(minimumUsers: number, maximumUsers: number | null | undefined): string {
    if (minimumUsers <= 0 && maximumUsers == null) {
      return this.formatNumber(0);
    }

    if (maximumUsers == null) {
      return this.translation.instant('ORG_SETTINGS.EMPLOYEE_BAND_MORE_THAN', {
        min: this.formatNumber(Math.max(0, minimumUsers - 1)),
      });
    }

    return this.translation.instant('ORG_SETTINGS.EMPLOYEE_BAND_RANGE', {
      min: this.formatNumber(minimumUsers),
      max: this.formatNumber(maximumUsers),
    });
  }

  protected saveOperational(): void {
    this.saveError.set(null);
    if (!this.canManage()) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.saveError.set(this.translation.instant(this.invalidFormMessageKey()));
      return;
    }
    const raw = this.form.getRawValue();
    const maxPast = raw.requests.leaveSubmission.maxPastDays;
    const payload: TenantOperationalConfigurationRoot = {
      attendance: {
        policy: {
          ...raw.attendance.policy,
          earliestCheckInMinutesBeforeShift: this.optionalWholeNumber(raw.attendance.policy.earliestCheckInMinutesBeforeShift),
          halfDayMinWorkedHours: this.optionalHours(raw.attendance.policy.halfDayMinWorkedHours),
          workdayMinWorkedHours: this.optionalHours(raw.attendance.policy.workdayMinWorkedHours),
        },
        geofence: { ...raw.attendance.geofence },
        faceRecognition: { ...raw.attendance.faceRecognition },
        absenceJob: { ...raw.attendance.absenceJob },
        absenceImpact: { ...raw.attendance.absenceImpact },
        travelMission: { ...raw.attendance.travelMission },
        hourlyLeavePermission: this.normalizeHourlyLeavePermission(raw.attendance.hourlyLeavePermission),
      },
      requests: {
        leaveSubmission: {
          allowPastDates: raw.requests.leaveSubmission.allowPastDates,
          maxPastDays: maxPast === null || maxPast === undefined || Number.isNaN(Number(maxPast)) ? null : Number(maxPast),
        },
      },
      payroll: this.normalizePayrollPeriodOptions(raw.payroll),
    };

    this.saving.set(true);
    this.api.updateOperationalSettings(payload).subscribe({
      next: () => {
        this.saving.set(false);
        this.toast.successKey('COMMON.SAVED');
        this.capturePendingChangesBaseline();
      },
      error: (err) => {
        this.saving.set(false);
        this.saveError.set(extractApiErrorMessage(err, apiErrorFallback(this.translation)));
      },
    });
  }

  private invalidFormMessageKey(): string {
    const attendance = this.form.controls.attendance.controls;
    if (attendance.absenceImpact.hasError('absenceFallbackRequired')) {
      return 'ORG_SETTINGS.ABSENCE_FALLBACK_REQUIRED';
    }
    if (attendance.policy.hasError('workdayRatioNotGreaterThanHalfDay')) {
      return 'ORG_SETTINGS.ATTENDANCE_RATIO_INVALID';
    }
    if (attendance.policy.hasError('workdayHoursNotGreaterThanHalfDay')) {
      return 'ORG_SETTINGS.ATTENDANCE_HOURS_ORDER_INVALID';
    }
    return 'ORG_SETTINGS.FORM_INVALID';
  }

  private static attendancePolicyRatioValidator(control: AbstractControl): ValidationErrors | null {
    const halfDay = Number(control.get('halfDayMinWorkedRatio')?.value);
    const workday = Number(control.get('absentCheckInAfterRatio')?.value);

    if (!Number.isFinite(halfDay) || !Number.isFinite(workday)) {
      return null;
    }

    return workday > halfDay ? null : { workdayRatioNotGreaterThanHalfDay: true };
  }

  private static attendanceHoursOrderValidator(control: AbstractControl): ValidationErrors | null {
    const halfDay = control.get('halfDayMinWorkedHours')?.value;
    const workday = control.get('workdayMinWorkedHours')?.value;
    if (halfDay === null || halfDay === undefined || halfDay === '' || workday === null || workday === undefined || workday === '') {
      return null;
    }
    return Number(workday) > Number(halfDay) ? null : { workdayHoursNotGreaterThanHalfDay: true };
  }

  private static absenceImpactFallbackValidator(control: AbstractControl): ValidationErrors | null {
    const action = Number(control.get('action')?.value);
    const fallback = Number(control.get('insufficientLeaveFallbackAction')?.value);
    if (action !== 1) {
      return null;
    }

    return fallback === 2 || fallback === 3 ? null : { absenceFallbackRequired: true };
  }

  protected hasPendingChanges(): boolean {
    return this.serializeOperationalForm() !== this.operationalBaseline;
  }

  private capturePendingChangesBaseline(): void {
    markPendingChangesSaved(this);
    this.operationalBaseline = this.serializeOperationalForm();
  }

  private serializeOperationalForm(): string {
    return JSON.stringify(this.form.getRawValue());
  }

  private withCurrentNumericOption(
    current: number,
    baseOptions: readonly NumericOption[],
    currentLabelKey: string,
  ): readonly NumericOption[] {
    if (!Number.isFinite(current)) {
      return baseOptions;
    }

    const hasExactOption = baseOptions.some((option) =>
      Math.abs(option.value - current) < 0.0001,
    );
    if (hasExactOption) {
      return baseOptions;
    }

    return [
      {
        value: current,
        labelKey: currentLabelKey,
        params: { percent: Math.round(current * 100) },
      },
      ...baseOptions,
    ];
  }

  private normalizeAttendancePolicy(policy: AttendancePolicyOptions): AttendancePolicyOptions {
    const halfDay = Number(policy.halfDayMinWorkedRatio);
    const workday = Number(policy.absentCheckInAfterRatio);
    if (this.isLegacyWorkdayRatioPolicy(halfDay, workday)) {
      return {
        ...policy,
        halfDayMinWorkedRatio: 0.5,
        absentCheckInAfterRatio: halfDay,
      };
    }

    if (!Number.isFinite(halfDay) || !Number.isFinite(workday) || workday > halfDay) {
      return { ...policy };
    }

    const nextWorkday = this.attendanceRatioBaseOptions
      .map((option) => option.value)
      .filter((value) => value > halfDay)
      .sort((a, b) => a - b)[0];

    return {
      ...policy,
      absentCheckInAfterRatio: nextWorkday ?? policy.absentCheckInAfterRatio,
    };
  }

  private isLegacyWorkdayRatioPolicy(halfDay: number, workday: number): boolean {
    return Number.isFinite(halfDay) &&
      Number.isFinite(workday) &&
      halfDay > 0.5 &&
      (workday >= 1 || workday <= halfDay);
  }

  /** Empty input means "no limit" (null); anything else is a whole, non-negative number. */
  private optionalWholeNumber(value: number | null | undefined): number | null {
    if (value === null || value === undefined || String(value).trim() === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : null;
  }

  /** Empty means "no limit" (null); otherwise a whole number of days, at least 1. */
  private optionalDayLimit(value: number | null | undefined): number | null {
    const parsed = this.optionalWholeNumber(value);
    return parsed === null || parsed < 1 ? null : parsed;
  }

  protected hourlyPermissionDayLimitInvalid(controlName: 'maxPastDays' | 'maxFutureDays'): boolean {
    const control = this.form.controls.attendance.controls.hourlyLeavePermission.controls[controlName];
    return control.invalid && (control.dirty || control.touched);
  }

  /** Empty means "use the share of the scheduled time" (null); otherwise hours rounded to two decimals. */
  private optionalHours(value: number | null | undefined): number | null {
    if (value === null || value === undefined || String(value).trim() === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : null;
  }

  private ratioPercent(ratio: number | null | undefined, fallback: number): number {
    const value = Number(ratio);
    return Number.isFinite(value) && value >= 0 && value <= 1 ? Math.round(value * 100) : fallback;
  }

  /** The policy fields this form edits; anything else the server returns stays out of the form. */
  private editablePolicy(policy: AttendancePolicyOptions): Partial<AttendancePolicyOptions> {
    return {
      lateGraceMinutes: policy.lateGraceMinutes,
      earlyLeaveGraceMinutes: policy.earlyLeaveGraceMinutes,
      earliestCheckInMinutesBeforeShift: policy.earliestCheckInMinutesBeforeShift ?? null,
      halfDayMinWorkedRatio: policy.halfDayMinWorkedRatio,
      absentCheckInAfterRatio: policy.absentCheckInAfterRatio,
      halfDayMinWorkedHours: policy.halfDayMinWorkedHours ?? null,
      workdayMinWorkedHours: policy.workdayMinWorkedHours ?? null,
    };
  }

  protected attendanceHoursInvalid(controlName: 'halfDayMinWorkedHours' | 'workdayMinWorkedHours'): boolean {
    const control = this.form.controls.attendance.controls.policy.controls[controlName];
    return control.invalid && (control.dirty || control.touched);
  }

  protected attendanceHoursOrderInvalid(): boolean {
    const policy = this.form.controls.attendance.controls.policy;
    return policy.hasError('workdayHoursNotGreaterThanHalfDay') && (policy.dirty || policy.touched);
  }

  protected earliestCheckInInvalid(): boolean {
    const control = this.form.controls.attendance.controls.policy.controls.earliestCheckInMinutesBeforeShift;
    return control.invalid && (control.dirty || control.touched);
  }

  private defaultHourlyLeavePermission(): HourlyLeavePermissionOptions {
    return {
      enabled: false,
      monthlyQuotaMinutes: 240,
      minRequestMinutes: 60,
      maxRequestMinutes: 120,
      requireReason: false,
      allowPastDates: false,
      maxPastDays: null,
      allowFutureDates: true,
      maxFutureDays: null,
    };
  }

  private normalizeHourlyLeavePermission(options: HourlyLeavePermissionOptions): HourlyLeavePermissionOptions {
    return {
      enabled: Boolean(options.enabled),
      monthlyQuotaMinutes: Math.max(0, Math.trunc(Number(options.monthlyQuotaMinutes) || 0)),
      minRequestMinutes: Math.max(0, Math.trunc(Number(options.minRequestMinutes) || 0)),
      maxRequestMinutes: Math.max(0, Math.trunc(Number(options.maxRequestMinutes) || 0)),
      requireReason: Boolean(options.requireReason),
      allowPastDates: Boolean(options.allowPastDates),
      maxPastDays: options.allowPastDates ? this.optionalDayLimit(options.maxPastDays) : null,
      allowFutureDates: Boolean(options.allowFutureDates),
      maxFutureDays: options.allowFutureDates ? this.optionalDayLimit(options.maxFutureDays) : null,
    };
  }

  private defaultPayrollPeriodOptions(): PayrollPeriodOptions {
    return {
      cycleStartDay: 1,
      useLastDayFallback: true,
      generationLocalTime: '03:00',
      timeZoneId: 'Africa/Cairo',
      labelMode: 1,
      autoGenerateAtPeriodStart: true,
    };
  }

  private normalizePayrollPeriodOptions(options: PayrollPeriodOptions): PayrollPeriodOptions {
    const cycleStartDay = Math.min(31, Math.max(1, Math.trunc(Number(options.cycleStartDay) || 1)));
    const generationLocalTime = String(options.generationLocalTime || '').trim() || '03:00';
    const timeZoneId = String(options.timeZoneId || '').trim() || 'UTC';
    return {
      cycleStartDay,
      useLastDayFallback: Boolean(options.useLastDayFallback),
      generationLocalTime,
      timeZoneId,
      labelMode: 1,
      autoGenerateAtPeriodStart: Boolean(options.autoGenerateAtPeriodStart),
    };
  }

  private syncTravelMissionDependentControls(enabled: boolean, emitEvent = true): void {
    const group = this.form.controls.attendance.controls.travelMission.controls;
    const options = { emitEvent };
    const controls = [
      group.createOnOffDays,
      group.skipIfAttendanceExists,
      group.useShiftWindow,
    ];

    for (const control of controls) {
      if (enabled) {
        control.enable(options);
      } else {
        control.disable(options);
      }
    }
  }
}
